import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';

import type { TypesenseConfig } from '#config/typesense';

import { INSTANT_SEARCH_CACHE_SECONDS, INSTANT_SEARCH_TOPIC_FILTER } from '#src/app/pages/search/shared/consts';

const SEARCH_TOPIC_FACET_VALUES_LIMIT = 30;

/**
 * Tag facet values for the petition search index, ordered like the search page Topics filter:
 * `count:desc`, then `name:asc` (see useTopicsFilter / useRefinementList).
 */
export async function fetchSearchTopicFacetValues(params: {
	readonly config: TypesenseConfig;
	readonly collectionName: string;
	readonly instantSearchPreset: string;
}): Promise<readonly string[]> {
	const { config, collectionName, instantSearchPreset } = params;

	const adapter = new TypesenseInstantsearchAdapter({
		server: config,
		additionalSearchParameters: {
			preset: instantSearchPreset,
		},
	});

	// Raw `typesenseClient.documents().search` does not use `additionalSearchParameters`; repeat preset (and query_by) on the request body.
	const response = await adapter.typesenseClient.collections(collectionName).documents().search(
		{
			q: '*',
			query_by: '*',
			preset: instantSearchPreset,
			per_page: 0,
			facet_by: INSTANT_SEARCH_TOPIC_FILTER,
			max_facet_values: SEARCH_TOPIC_FACET_VALUES_LIMIT,
		},
		{ cacheSearchResultsForSeconds: INSTANT_SEARCH_CACHE_SECONDS },
	);

	const rawCounts = response.facet_counts?.find((f) => String(f.field_name) === INSTANT_SEARCH_TOPIC_FILTER)?.counts;

	if (!rawCounts?.length) return [];

	const sorted = [...rawCounts];
	let groupStart = 0;
	for (let i = 1; i <= sorted.length; i += 1) {
		const isGroupBoundary = i === sorted.length || sorted[i].count !== sorted[groupStart].count;
		if (isGroupBoundary) {
			if (i - groupStart > 1) {
				const sortedGroup = [...sorted.slice(groupStart, i)].sort((a, b) =>
					String(a.value).localeCompare(String(b.value), undefined, { sensitivity: 'base' }),
				);
				sorted.splice(groupStart, i - groupStart, ...sortedGroup);
			}
			groupStart = i;
		}
	}

	return sorted.slice(0, SEARCH_TOPIC_FACET_VALUES_LIMIT).map((c) => String(c.value));
}
